import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InboxNotification, NotificationInboxFilter, NotificationInboxService } from 'src/app/shared/collaboration/notification-inbox.service';
import { PushNotificationService } from 'src/app/shared/collaboration/push-notification.service';
import { formatLocalDateTime as formatLocalDateTimeValue } from 'src/app/shared/constants/date-utils';

type NotificationDialogTab = 'received' | 'configuration';

@Component({
  standalone: false,
  selector: 'app-home-notifications-dialog',
  templateUrl: './home-notifications-dialog.component.html',
  styleUrls: ['./home-notifications-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeNotificationsDialogComponent implements OnInit {
  private readonly inboxService = inject(NotificationInboxService);
  private readonly pushService = inject(PushNotificationService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly activeTab = signal<NotificationDialogTab>('received');
  readonly activeFilter = signal<NotificationInboxFilter>('all');
  readonly notifications = signal<InboxNotification[]>([]);
  readonly pageIndex = signal(0);
  readonly pageSize = 30;
  readonly total = signal(0);
  readonly loading = signal(true);
  readonly savingNotificationId = signal<number | null>(null);
  readonly savingAllNotifications = signal(false);
  readonly errorMessage = signal('');
  readonly unreadCount = this.inboxService.unreadCount;
  readonly hasMore = computed(() => this.notifications().length < this.total());

  ngOnInit(): void {
    this.loadNotifications(true);
    this.pushService.notificationReceived$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.activeTab() === 'received') this.loadNotifications(true);
      });
  }

  setTab(tab: NotificationDialogTab): void {
    this.activeTab.set(tab);
    if (tab === 'received') this.loadNotifications(true);
  }

  setFilter(filter: NotificationInboxFilter): void {
    if (this.activeFilter() === filter) return;
    this.activeFilter.set(filter);
    this.loadNotifications(true);
  }

  loadNotifications(reset: boolean): void {
    const nextPage = reset ? 0 : this.pageIndex() + 1;
    this.loading.set(true);
    this.errorMessage.set('');
    this.inboxService.list(nextPage, this.pageSize, this.activeFilter())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: page => {
          this.pageIndex.set(page.pageIndex);
          this.total.set(page.total);
          this.notifications.set(reset ? page.items : [...this.notifications(), ...page.items]);
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set('No fue posible cargar las notificaciones.');
          this.loading.set(false);
        },
      });
  }

  toggleReadState(notification: InboxNotification): void {
    const nextRead = !notification.readAt;
    this.savingNotificationId.set(notification.id);
    this.inboxService.setReadState(notification.id, nextRead)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const readAt = nextRead ? new Date().toISOString() : null;
          this.notifications.update(items => items.map(item =>
            item.id === notification.id ? { ...item, readAt } : item,
          ));
          this.savingNotificationId.set(null);
          if ((this.activeFilter() === 'unread' && nextRead) || (this.activeFilter() === 'read' && !nextRead)) {
            this.loadNotifications(true);
          }
        },
        error: () => {
          this.errorMessage.set('No fue posible actualizar el estado de la notificaci\u00f3n.');
          this.savingNotificationId.set(null);
        },
      });
  }

  markAllAsRead(): void {
    if (this.unreadCount() === 0 || this.savingAllNotifications()) return;

    this.savingAllNotifications.set(true);
    this.inboxService.markAllAsRead()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const readAt = new Date().toISOString();
          this.notifications.update(items => items.map(item =>
            item.readAt ? item : { ...item, readAt },
          ));
          this.savingAllNotifications.set(false);
          this.loadNotifications(true);
        },
        error: () => {
          this.errorMessage.set('No fue posible marcar las notificaciones como le\u00eddas.');
          this.savingAllNotifications.set(false);
        },
      });
  }

  formatDate(value: string | null | undefined): string {
    return formatLocalDateTimeValue(value);
  }

  openNotification(notification: InboxNotification): void {
    if (!notification.url) return;
    this.dialogRef.close();
    void this.router.navigateByUrl(notification.url);
  }
}
